"use client";

import type { MutationOptions } from "@tanstack/react-query";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import gql from "graphql-tag";
import type { GeneratedMutation, GraphQLResult } from "@aws-amplify/api-graphql";
import type { GeneralResponse, StoreOnboardingSectionInput } from "@rmh/defs";
import { fetchGraphQLClient } from "../../../amplify/fetchGraphQLClient";
import { onboardingSectionQueryKey } from "./keys";

interface StoreOnboardingSectionMutationVariables {
  input: StoreOnboardingSectionInput;
}

interface StoreOnboardingSectionMutationData {
  storeOnboardingSection: GeneralResponse;
}

export const storeOnboardingSectionMutation = gql`
  mutation storeOnboardingSection($input: StoreOnboardingSectionInput!) {
    storeOnboardingSection(input: $input) {
      success
      message
      errorMsg
    }
  }
`.loc?.source.body as GeneratedMutation<
  StoreOnboardingSectionMutationVariables,
  StoreOnboardingSectionMutationData
>;

/**
 * Store an onboarding-v2 section to Canvas (write-through) over the
 * Cognito-authed client (default `authMode: lambda`). The resolver derives the
 * patient identity from the verified token, so a client cannot write against
 * another patient. On success the section's cached read is invalidated under
 * the shared key, so a subsequent `useGetOnboardingSection` refetches the
 * canonical value from Canvas rather than trusting a locally-seeded copy.
 */
export const useStoreOnboardingSection = (
  options: MutationOptions<
    GraphQLResult<StoreOnboardingSectionMutationData>,
    Error,
    StoreOnboardingSectionMutationVariables
  > = {}
) => {
  const queryClient = useQueryClient();

  return useMutation<
    GraphQLResult<StoreOnboardingSectionMutationData>,
    Error,
    StoreOnboardingSectionMutationVariables
  >({
    ...options,
    mutationFn: (variables) =>
      fetchGraphQLClient(storeOnboardingSectionMutation, variables),
    onSuccess: (...args) => {
      const { patientId, section } = args[1].input;
      queryClient.invalidateQueries({
        queryKey: onboardingSectionQueryKey(patientId, section),
      });
      options.onSuccess?.(...args);
    },
  });
};
